import * as Sentry from '@sentry/react'
import Cookies from 'js-cookie'
import { createRoot } from 'react-dom/client'
import { AUTH_COOKIE_NAME } from '@knitknot/shared/constants'
import App from './App.tsx'
// Canonical theme (MD scale + light/dark tokens + shared component CSS).
// Single source of truth lives in packages/shared/styles/base.css; the
// console previously shipped a byte-identical duplicate as ./index.css.
import '@knitknot/shared/styles/base.css'
// Self-hosted Inter (variable font, all weights in one file) — replaces the
// render-blocking Google Fonts <link> (see index.html). Registers the
// 'Inter Variable' family; the font stack in packages/shared/tailwind/preset.js
// + base.css lists 'Inter Variable' ahead of 'Inter' so this resolves first.
// The `opsz` build, not the default `wght`-only one: it carries BOTH axes
// (opsz 14-32 + wght 100-900), which enables `font-optical-sizing: auto` in
// base.css. Inter's optical-size axis is what "Inter Display" is — no extra
// font dependency needed.
import '@fontsource-variable/inter/opsz.css'
import { logger } from './services/logger'
import { scheduleIdle } from './lib/idle'
import { scheduleRum } from './lib/rum'
import { queryClient } from './lib/queryClient'
import { setPersistScopeIds } from './lib/queryPersister'
import { queryKeys } from './lib/queryKeys'
import { getUserIdFromJWT } from './services/auth'
import { workspaceService } from './services/workspace'
import { STALE } from './lib/queryPolicy'

// Initialize Sentry with errors only. DSN unset → SDK no-ops (safe for tests /
// local dev without observability).
//
// Session Replay (rrweb-based recording, the bulk of Sentry's ~25% share of
// the entry bundle) is deferred: added via Sentry's CDN lazy-load mechanism
// (`Sentry.lazyLoadIntegration`) on idle, so it's never bundled by Vite at
// all. Its sample-rate options must stay here in init() — the lazy-loaded
// integration reads them off the client's options, only the integration code
// itself moves.
//
// browserTracingIntegration stays in the eager init below: it is NOT in
// Sentry's CDN lazy-loadable allowlist (`lazyLoadIntegration('browserTracingIntegration')`
// throws "Cannot lazy load integration"), and since @sentry/react is already
// statically imported here for Sentry.init/getClient/lazyLoadIntegration,
// deferring the addIntegration call wouldn't shrink the bundle anyway — it
// would only risk missing the automatic pageload transaction, which needs to
// hook in at init time. So there's no size win available for it; only Replay
// is deferred.
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
  // Trace propagation to backend so frontend → backend traces stitch.
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/api\.knitknot\.ai/,
  ],
  // Session Replay sampling. Captures full sessions for 10% of users +
  // 100% of users who hit an error (so we always have replay context).
  // Read by the client once the replay integration is added lazily below.
  replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SERVICE_VERSION || 'dev',
});

// Lazy-load Session Replay once the browser is idle (or after a 2s fallback
// timeout), instead of paying its parse/exec cost on every page load.
scheduleIdle(() => {
  Sentry.lazyLoadIntegration('replayIntegration')
    .then((replayIntegration) => {
      Sentry.getClient()?.addIntegration(replayIntegration());
    })
    .catch((err) => {
      logger.warn({ category: 'app', err }, 'Sentry Session Replay lazy-load failed');
    });
});

// web-vitals RUM (perf plan WS6.2) — idle-loaded, batched, sendBeacon'd.
scheduleRum();

// Boot fetch before React mounts (perf plan §4.8). Fires the workspace-list +
// current-workspace-detail requests immediately — before the router mounts,
// ProtectedRoute resolves, and useWorkspaces/useWorkspaceSwitcher's own
// effects would otherwise kick them off — so by the time those hooks read
// the SAME query keys, the request is already in flight (or resolved).
// Skipped entirely when there's no auth cookie (signed-out boot: nothing to
// fetch, and firing here would just 401 and log noise before SignIn mounts).
if (Cookies.get(AUTH_COOKIE_NAME)) {
  const bootUserId = getUserIdFromJWT();
  // Register the boot identity as valid persist scopes (queryPersister's
  // tenant-scoping guard only persists keys under these ids).
  setPersistScopeIds([bootUserId, Cookies.get('workspace_id')]);
  if (bootUserId) {
    void queryClient.prefetchQuery({
      queryKey: queryKeys.workspace.list(bootUserId),
      queryFn: () => workspaceService.listUserWorkspaces(bootUserId),
      staleTime: STALE.stable,
    });
  }
  const bootWorkspaceId = Cookies.get('workspace_id');
  if (bootWorkspaceId) {
    void queryClient.prefetchQuery({
      queryKey: queryKeys.workspace.detail(bootWorkspaceId),
      queryFn: () => workspaceService.getWorkspace(bootWorkspaceId),
      staleTime: STALE.stable,
    });
  }
}

// Log application startup
logger.info({ category: 'app' }, 'KnitKnot frontend starting');

createRoot(document.getElementById("root")!).render(<App />);
